import { unitDue } from '../data/studentMock';
import { dueEndMs, shortDue } from '../../TeacherDashboardV3/data/unitPlan';
import { useAuth } from '../../Accounts/AuthContext';
import { assignmentSlotSummary, isOpenAssignmentSlot } from '../data/assignmentSlots';

// The one way the class unit is communicated everywhere (start page, subject
// page, domain page). Two paths:
//
// REAL (assignment prop, from getAssignments): the unit-status grammar from
// DESIGN.md — gap-separated tokens, no middots:
//   [dot] On track   2 of 4 parts   Mixed practice due Fri, Jul 17
// The part labels come from the assignment's own slots (the teacher's real
// sequence), never a hardcoded order.
//
// MOCK (no prop, signed-out demo): the next assignment and its due date,
// stated plainly: "Rhetorical Synthesis due Fri, Jul 17".

export function UnitDot({ onTrack }) {
  return (
    <span
      className="w-2 h-2 rounded-full flex-none"
      style={{ background: onTrack == null ? '#a3a3a3' : onTrack ? '#22c55e' : '#f59e0b' }}
    />
  );
}

export default function UnitStatus({ assignment }) {
  const { user } = useAuth();
  if (assignment) {
    const { slots, known, unknown, doneCount, next } = assignmentSlotSummary(assignment);
    if (!slots.length) return null;
    // Behind = an unfinished slot whose due has CLOSED (dueEndMs: end of the
    // local day for date-only dues, the exact moment for timed ones).
    const behind = slots.some((s) => isOpenAssignmentSlot(s) && dueEndMs(s.due) < Date.now());
    const partLabel = assignment.checkpointMode === 'continuous' ? 'checkpoints' : 'parts';
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] font-medium text-neutral-500">
        <span className="inline-flex items-center gap-1.5">
          <UnitDot onTrack={behind ? false : unknown.length ? null : true} />
          {behind ? 'Behind' : unknown.length ? 'Progress partly unavailable' : 'On track'}
        </span>
        {known.length > 0 && <span>
          <span className="font-bold text-ink tabular-nums">{doneCount} of {known.length}</span> {unknown.length ? `confirmed ${partLabel}` : partLabel}
        </span>}
        {unknown.length > 0 && <span>{unknown.length} checkpoint {unknown.length === 1 ? 'result unavailable' : 'results unavailable'}</span>}
        {next && (
          <span>
            <span className="font-bold text-ink">{next.label || 'Assignment'}</span>
            {' due '}
            <span className="font-bold text-ink">{shortDue(next.due)}</span>
          </span>
        )}
      </div>
    );
  }

  // Mock path: signed-out demo only. A signed-in caller with no real
  // assignment (e.g. mid-load) renders nothing here, never the demo's due line.
  if (user) return null;
  const due = unitDue();
  if (!due) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] font-medium text-neutral-500">
      <span className="font-bold text-ink">{due.label}</span>
      <span>due</span>
      <span className="font-bold text-ink">{due.dueLong}</span>
    </div>
  );
}
